// App.js - Updated with multi-country support, fixed routing, and theme support
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './theme.css'; // Import theme system
import 'bootstrap/dist/css/bootstrap.min.css';
import { AnimatePresence } from 'framer-motion';

import { SearchProvider } from './components/navigation/SearchContext';
import { applyStoredTheme, watchSystemTheme, selectTheme, setAutoEnabled } from './utils/themeUtils';
import CanadaHomePage from './components/pages/CanadaHomePage';
import SectionPage from './components/pages/SectionPage';
import FullFeedPage from './components/feed/FullFeedPage';
import FeedItemDetail from './components/feed/FeedItemDetail';
import StatePage from './components/pages/StatePage';

import DepartmentRoutes from './routes/DepartmentRoutes';
import MainDepartmentPage from './components/pages/MainDepartmentPage';
import AgencyPage from './components/pages/AgencyPage';
import SearchPage from './components/pages/SearchPage';
import CollectionPage from './components/pages/CollectionPage';

import MexicoHomePage from './components/pages/MexicoHomePage';
import ProvincePage from './components/pages/ProvincePage';
import MexicoStatePage from './components/pages/MexicoStatePage';
import UKHomePage from './components/pages/UKHomePage';
import UKKingdomPage from './utils/UKKingdomPage';
import NewCountryHomePage from './components/pages/NewCountryHomePage';
import NotificationsOnboarding from './components/notifications/NotificationsOnBoarding';
import MinimalPushTest from './components/notifications/MinimalPushTest';

import { getFeedsConfig, getSectionsInfo } from './utils/multiCountryConfig.js';
import NavbarWrapper from './components/navigation/NavbarWrapper';
import NotificationPage from './components/pages/NotificationPage';
import NotificationSettingsPage from './components/pages/NotificationSettingsPage';
import CountryRecentsPage from './components/pages/CountryRecentsPage';
import RecentsPage from './components/pages/RecentsPage';
import HousePage from './components/pages/HousePage';
import CommitteePage from './components/pages/CommitteePage';
import SenatePage from './components/pages/SenatePage';
import { Spinner, ThemeProvider } from 'react-bootstrap';
import PageTransition from './transition/PageTransition';
import {
  HouseOfRepsMembersPage,
  SenateMembersPage,
  PresidentsPage,
  JusticesPage,
  VicePresidentsPage,
  MemberDetailPage,
  PresidentDetailPage,
  VicePresidentDetailPage,
  JusticeDetailPage,
} from './components/members';
import LocationSettingsPage from './components/pages/LocationSettingsPage';
import IntegratedSearchNavBar from './components/navigation/IntegratedSearchNavBar';
import Footer from './components/layout/Footer';
import { migrateOldCollection } from './utils/migrateCollection';
import Home from './components/pages/Home';
import AboutPage from './components/pages/AboutPage';
import Privacy from './components/pages/Privacy';
import LocalReportsPage from './reports/LocalReportsPage';
import migrateOnboardingData from './components/notifications/onboardingMigration';
import SocialPage from './components/pages/SocialPage';
import ThemeSettings from './components/pages/ThemeSettings';
import LocalPage from './components/pages/LocalPage';
import USHomePage from './components/pages/USHomePage';
import FactCheckerPage from './components/pages/FactCheckerPage';
import RepresentativesPage from './components/pages/RepresnativesPage';
import WhatsNewModal from './components/whatsnew/WhatsNewModal';
import NotificationBootstrap from './components/notifications/NotificationsBootstrap';
import NewThemePrompt from './components/theme/NewThemePrompt';
import StateCandidates from './components/members/StateCandidates';
import CandidateDetailPage from './components/members/CandidateDetailPage';
import StateCandidatesPage from './components/members/StateCandidatesPage';
import CandidatesPage from './components/members/CandidatesPage';
import LocalCandidateDetailPage from './components/members/LocalCandidateDetailPage';
import PartyPage from './components/elections/parties/PartyPage';
import CompanyPage from './components/spending/CompanyPage';
import ElectionsPage from './components/elections/ElectionsPage';
import SpendingDashboardPage from './components/spending/SpendingDashboardPage';
import AwardsPage from './components/spending/AwardsPage';
import { getStateInfo } from './utils/feedsConfig';


// Topic URLs, rendered by the country home pages rather than SectionPage. Each
// home page reads the topic off the path, so /us/legislative shows the same view
// the feed filter bar shows and pasting the URL lands on that topic directly.
//
// Each list must match the viewAllLink values in that country's SECTION_MAP
// (plus, for the US, home-page views with a URL of their own, such as
// /us/live-streams), and only covers topics the home page can actually render (present in both
// SECTION_MAP and DESKTOP_SECTION_MAP). Topics missing from a country's maps
// keep their SectionPage route below — Canada's defence/health/immigration/
// infrastructure/justice and Mexico's immigration/justice.
//
// The lists also tell AppLayout not to add a second navbar, since the home pages
// render their own IntegratedSearchNavBar.
export const US_SECTION_PATHS = [
  '/us/legislative',
  '/us/executive',
  '/us/judicial',
  '/us/defense',
  '/us/elections',
  '/us/economy',
  '/us/environment',
  '/us/foreignaffairs',
  '/us/health',
  '/us/immigration',
  '/us/regulations',
  '/us/infrastructure',
  '/us/justice',
  '/us/science&tech',
  '/us/spending',
  // Not a SECTION_MAP topic: the Live streams view, which USHomePage maps to
  // its 'videos' topic itself (see HOME_VIEW_ROUTES there). Listed here so the
  // path renders the home page and AppLayout adds no second navbar.
  '/us/live-streams',
];

export const CA_SECTION_PATHS = [
  '/canada/legislative',
  '/canada/executive',
  '/canada/judicial',
  '/canada/economy',
  '/canada/environment',
  '/canada/foreignaffairs',
  '/canada/science&tech',
];

export const MX_SECTION_PATHS = [
  '/mexico/legislative',
  '/mexico/executive',
  '/mexico/judicial',
  '/mexico/defense',
  '/mexico/economy',
  '/mexico/environment',
  '/mexico/foreignaffairs',
  '/mexico/health',
  '/mexico/infrastructure',
];

export const UK_SECTION_PATHS = [
  '/uk/legislative',
  '/uk/executive',
  '/uk/judicial',
  '/uk/defense',
  '/uk/economy',
  '/uk/environment',
  '/uk/foreignaffairs',
  '/uk/health',
  '/uk/infrastructure',
  '/uk/justice',
  '/uk/science&tech',
];

const ALL_SECTION_PATHS = [
  ...US_SECTION_PATHS,
  ...CA_SECTION_PATHS,
  ...MX_SECTION_PATHS,
  ...UK_SECTION_PATHS,
];

// StatePage's own sub-routes. /us/:stateName/legislative and its two siblings
// render StatePage as well, so the navbar check below has to reach them; every
// other two-segment /us/ path (/us/senate/:committeeId,
// /us/representative/:bioGuideId) must NOT match, which is why these are named
// rather than allowed as any second segment.
const STATE_BRANCH_SEGMENTS = 'legislative|executive|judicial';
const US_STATE_PATH_RE = new RegExp(
  `^\\/us\\/([^/]+)(?:\\/(?:${STATE_BRANCH_SEGMENTS}))?\\/?$`,
  'i'
);

// Onboarding appears on "/" and on the country home pages, and nowhere else.
// Any other URL — a topic view like /us/legislative, a state page, a feed item,
// a bill — renders its page straight away, even for someone who has never
// onboarded. They go through onboarding the next time they land on one of
// these paths.
//
// "/" goes through OnboardingWrapper (which also redirects onboarded users to
// their home country). The country home pages get onboarding as an overlay
// from CountryHomeOnboarding below, on top of the page itself.
//
// Matched exactly, so /us shows onboarding but /us/legislative (which renders
// the same USHomePage component) does not.
const COUNTRY_HOME_PATHS = [
  '/us',
  '/canada',
  '/mexico',
  '/uk',
  '/australia',
  '/germany',
  '/france',
  '/italy',
  '/israel',
  '/ukraine',
  '/china',
  '/japan',
  '/nigeria',
  '/south-africa',
];

// "/us/" and "/us" are the same page; anything longer is not a home page.
const normalizePath = (path) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

const isCountryHomePath = (path) => COUNTRY_HOME_PATHS.includes(normalizePath(path));

// Has this person finished the main onboarding flow? Used to keep
// onboarding-related side effects (the theme re-prompt and its forced theme
// switch) off first-time visitors who arrive on a deep link.
const hasCompletedOnboarding = () => {
  try {
    return localStorage.getItem("onboarding") === "true";
  } catch {
    return false;
  }
};

// Does NotificationsOnboarding have anything to show? Mirrors its own mount
// logic and OnboardingWrapper's: all three flags must be set to skip it. A
// missing themeOnboarding or languageOnboarding opens it in theme-only or
// language-only mode rather than the full flow.
const ONBOARDING_FLAGS = ["onboarding", "themeOnboarding", "languageOnboarding"];
const needsOnboarding = () => {
  try {
    return ONBOARDING_FLAGS.some((key) => localStorage.getItem(key) !== "true");
  } catch {
    return false;
  }
};

// Run migration on app load
const migrationResult = migrateOnboardingData();
if (migrationResult.hasMigrated) {
  console.log("Migration completed:", migrationResult);
}

// One-time re-prompt for theme selection. Bumping this version clears only the
// themeOnboarding flag, so already-onboarded users go back through the theme
// step (and nothing else) exactly once per version, the next time they open "/"
// or a country home page.
//
// Everyone who has onboarded is moved onto Classic Light at the same time, so
// the picker opens showing the new theme rather than whatever they had before.
// Auto has to go off for that to actually stick: with Auto on, the device
// decides which side shows, so a phone in dark mode would land on the dark pick
// and never see Classic Light. Auto can be turned back on from Preferences.
//
// Someone who has never onboarded (typically a first visit through a shared
// link) is not re-prompted: they have no old theme to move off, and their own
// onboarding will ask for a theme anyway. Their theme is left alone so the
// deep-linked page renders in their stored/default theme, and the version is
// still stamped so finishing onboarding later doesn't immediately trigger a
// re-prompt for a version they never had.
const ONBOARDING_VERSION = "v4";
if (localStorage.getItem("onboardingVersion") !== ONBOARDING_VERSION) {
  if (hasCompletedOnboarding()) {
    setAutoEnabled(false);
    selectTheme('classic-light');
    localStorage.removeItem("themeOnboarding");  // Only reset theme, not full onboarding
  }
  localStorage.setItem("onboardingVersion", ONBOARDING_VERSION);
}

// Initialize theme on app load (before React renders), then keep it following
// the device while Auto is on. themeUtils owns the data-theme attribute; the
// old inline initializeTheme() block and its matchMedia listener that lived here
// read a bare "theme" key and fought this for the same attribute, so they are
// gone rather than running first and causing a flash of the wrong theme.
applyStoredTheme();
watchSystemTheme();


// Onboarding on the country home pages. The page renders underneath as usual;
// NotificationsOnboarding is a full-screen fixed overlay, so it sits on top.
//
// The wrapper creates its own stacking context above the fixed navbars.
// Bootstrap's fixed-top/fixed-bottom bars sit at z-index 1030, above the
// overlay's own 1000, and would otherwise paint over the onboarding modal.
// Raising the wrapper instead of the overlay leaves NotificationsOnboarding
// untouched, and it still positions against the viewport as before.
//
// Re-evaluated on every navigation. Finishing onboarding writes the flags and
// then navigates to the chosen home country, so the next render sees the flags
// and unmounts this. Onboarding picking a different country than the one
// the user was on (onboarding on /uk, choosing US) lands them on /us already
// onboarded.
const CountryHomeOnboarding = () => {
  const { pathname } = useLocation();

  if (!isCountryHomePath(pathname) || !needsOnboarding()) return null;

  return (
    <div style={{ position: 'relative', zIndex: 2000 }}>
      <NotificationsOnboarding />
    </div>
  );
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  
  const getCurrentCountry = () => {
    const path = location.pathname;
    if (path.startsWith('/canada')) return 'ca';
    if (path.startsWith('/mexico')) return 'mx';
    if (path.startsWith('/uk')) return 'uk';
    return 'us';
  };
  
  const currentCountry = getCurrentCountry();
  const isOnboarding = location.pathname === '/';

  // Re-read on every navigation (AppLayout re-renders when the location
  // changes), so finishing onboarding on "/" and being navigated to the home
  // page flips this without a reload.
  const onboarded = hasCompletedOnboarding();
  
  // Pages that have their own IntegratedSearchNavBar or custom navbar
  const pagesWithOwnSearchBar = [
    '/feed/',           // FullFeedPage
    '/collection',      // CollectionPage
    '/search',          // SearchPage
    '/notifications',   // NotificationPage (has custom navbar in GenerateNotificationFeed)  
    '/social' ,      
                          // NewCountryHomePage
    '/australia',
    '/germany',
    '/france',
    '/italy',
    '/israel',
    '/ukraine',
    '/china',
    '/japan',
    '/nigeria',
    '/south-africa',
  ];

  // StatePage and LocalPage mount their own IntegratedSearchNavBar. Without
  // this, AppLayout mounts a second one: the two bars paint on top of each
  // other and look right, but their spacers stack in flow and open a gap the
  // height of a whole navbar.
  //
  // State pages resolve the slug rather than trust the pattern alone —
  // /us/:stateName has the same shape as /us/local, /us/senate and
  // /us/houseofreps, and only feedsConfig knows which slugs are actually
  // states. The branch sub-routes are covered by US_STATE_PATH_RE above.
  const usSlug = location.pathname.match(US_STATE_PATH_RE)?.[1];
  const rendersOwnNavbar =
    location.pathname === '/us/local' ||
    Boolean(usSlug && getStateInfo(usSlug.toLowerCase()));

  // Check for recents pages and member pages
  const hasOwnSearchBar = 
    rendersOwnNavbar ||                                          // StatePage, LocalPage
    pagesWithOwnSearchBar.some(path => location.pathname.startsWith(path)) ||
    location.pathname === '/us' ||
    location.pathname === '/canada' ||
    location.pathname === '/mexico' ||
    location.pathname === '/uk' ||
    ALL_SECTION_PATHS.includes(location.pathname) ||   // country home page topic URLs
    location.pathname.endsWith('/recents') ||                    // RecentsPage
    location.pathname === '/us/houseofreps' ||                   // MembersFullPage
    location.pathname === '/us/senators' ||
    location.pathname === '/us/presidents' ||
    location.pathname === '/us/vicepresidents' ||
    location.pathname === '/us/justices';
    const showWhatsNew = new URLSearchParams(location.search).get("whatsnew") === "1";
  return (
    <>
      {!isOnboarding && !hasOwnSearchBar && <IntegratedSearchNavBar currentCountry={currentCountry} />}
      {/* Every page sits inside the same side gutters (.app-page in App.css),
          so no page runs flush to the screen edge. Onboarding is left
          full-bleed: it is laid out as a full-screen flow of its own. Fixed
          bars (the navbars, the bottom tabs) are positioned against the
          viewport and are unaffected. */}
      <div className={isOnboarding ? undefined : 'app-page'}>
        {children}
      </div>
      {/* Mounted on every path, including onboarding: it renders nothing until
          onboarding is finished, and stamps the current version for first-run
          users so a brand new install never sees the release notes. */}
      <WhatsNewModal forceOpen={showWhatsNew} />
      {/* Announces a new theme to people who already have one. Someone who
          arrived on a deep link without onboarding has never picked a theme,
          so the prompt would be an onboarding step in disguise on a page that
          isn't "/". Held back until they've onboarded. */}
      {onboarded && <NewThemePrompt />}
      <NotificationBootstrap />
      {!isOnboarding && <Footer />}
      {!isOnboarding && <NavbarWrapper />}
      {/* Last, so it stacks over the page and its chrome. Renders nothing
          outside the country home pages or once onboarding is complete. */}
      <CountryHomeOnboarding />
    </>
  );
};


const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};



export const memberRoutes = [
  // House of Representatives
  {
    path: '/us/houseofreps',
    element: <HouseOfRepsMembersPage />,
    description: 'Full list of House members with search',
  },
  {
    path: '/us/representative/:bioGuideId',
    element: <MemberDetailPage />,
    description: 'Individual House member detail page',
  },
  
  // Senate
  {
    path: '/us/senators',
    element: <SenateMembersPage />,
    description: 'Full list of Senate members with search',
  },
  {
    path: '/us/senator/:bioGuideId',
    element: <MemberDetailPage />,
    description: 'Individual Senator detail page',
  },
  
  // Presidents (placeholder for future)
  {
    path: '/us/presidents',
    element: <PresidentsPage />,
    description: 'List of Presidents',
  },
  {
    path: '/us/president/:bioGuideId',
    element: <MemberDetailPage />,
    description: 'Individual President detail page',
  },
  
  // Supreme Court Justices (placeholder for future)
  {
    path: '/us/justices',
    element: <JusticesPage />,
    description: 'List of Supreme Court Justices',
  },
  {
    path: '/us/justice/:bioGuideId',
    element: <MemberDetailPage />,
    description: 'Individual Justice detail page',
  },
];

export const generateMemberRoutes = () => {
  return memberRoutes.map((route) => (
    <Route 
      key={route.path} 
      path={route.path} 
      element={route.element} 
    />
  ));
};

// Component to conditionally render NavbarWrapper
const ConditionalNavbarWrapper = () => {
  const location = useLocation();
  
  // Don't show navbar on root path (onboarding)
  if (location.pathname === '/') {
    return null;
  }
  
  return <NavbarWrapper />;
};

// Wrapper component to handle onboarding redirect logic.
//
// Mounted on "/" only. The country home pages get onboarding from
// CountryHomeOnboarding instead; every other route renders its page directly
// regardless of onboarding state.
const OnboardingWrapper = () => {
  const navigate = useNavigate();
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const onboarding = localStorage.getItem("onboarding");
    const homeCountry = localStorage.getItem("homeCountry");
    const themeOnboarding = localStorage.getItem("themeOnboarding");
    const languageOnboarding = localStorage.getItem("languageOnboarding"); // new

    // Only skip the modal once ALL three flags are set
    if (
      onboarding === "true" &&
      themeOnboarding === "true" &&
      languageOnboarding === "true"
    ) {
      // Already completed onboarding, redirect
      if (homeCountry === "us") {
        navigate("/us", { replace: true });
      } else if (homeCountry === "ca") {
        navigate("/canada", { replace: true });
      } else if (homeCountry === "mx") {
        navigate("/mexico", { replace: true });
      } else if (homeCountry === "uk") {
        navigate("/uk", { replace: true });
      } else {
        navigate("/us", { replace: true });
      }
    } else {
      // Show onboarding (NotificationsOnboarding will decide which mode)
      setShouldShowOnboarding(true);
    }
    setIsChecking(false);
  }, [navigate]);

  if (isChecking) {
    return (
      <div className="app-loading-container">
        <Spinner id='spinner' animation="grow" className="themed-spinner" />
      </div>
    );
  }

  if (shouldShowOnboarding) {
    return <NotificationsOnboarding />;
  }

  return null;
};


const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
    <Routes location={location}>
      {/* Wrap all routes in a layout route */}
      <Route element={<PageTransition />}>
        {/* Root route with wrapper. Onboarding also shows on the country home
            pages, via CountryHomeOnboarding in AppLayout. */}
        <Route path="/" element={<OnboardingWrapper />} />
        <Route path="/all" element={<Home />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<Privacy/>} />
        {/* <Route path="/reports" element={<LocalReportsPage/>} /> */}
          {/* <Route path="/test" element={<MinimalPushTest/>} /> */}
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/social" element={<SocialPage />} />
          <Route path="/notifications" element={<NotificationPage />} />
          <Route path="/fact-check" element={<FactCheckerPage/>} />
          <Route path="/notifications/settings" element={<NotificationSettingsPage />} />
          <Route path="/preferences"  element={<LocationSettingsPage />} />
        
          {/* ==== UNITED STATES ROUTES ==== */}
          <Route path="/us" element={<USHomePage />} />
          <Route path="/us/local" element={<LocalPage/>} /> 


          <Route path="/us/candidate/:candidateId" element={<CandidateDetailPage/>} />
          <Route path="/us/candidates" element={<CandidatesPage />} />
<Route path="/us/candidates/:stateAbbr" element={<CandidatesPage />} />

          {/* One party, optionally scoped to a state with ?state=VA. The
              static "party" segment outranks /us/:stateName/..., so no state
              slug can shadow it. */}
          <Route path="/us/party/:partyKey" element={<PartyPage />} />

          {/* One company from the spending dashboard (/us/spending-dashboard). */}
          <Route path="/us/company/:orgId" element={<CompanyPage />} />

          {/* The Elections and Spending tabs in the bottom bar: the upcoming
              elections panel and the spending dashboard as pages of their
              own. Not /us/elections and /us/spending, which are the Federal
              feed's Elections and Spending topics. */}
          <Route path="/us/elections-dashboard" element={<ElectionsPage />} />
          <Route path="/us/spending-dashboard" element={<SpendingDashboardPage />} />
          {/* The full new-awards feed; the dashboard shows two and links here. */}
          <Route path="/us/spending-dashboard/awards" element={<AwardsPage />} />

          {/* US Topic Routes — home page view with the topic preselected */}
          {US_SECTION_PATHS.map((path) => (
            <Route key={path} path={path} element={<USHomePage />} />
          ))}


          

<Route path="/us/senate" element={<SenatePage />} />
<Route path="/us/senate/:committeeId" element={<CommitteePage chamber="senate" />} />


<Route path="/us/house/:committeeId" element={<CommitteePage chamber="house" />} />
<Route path="/us/house" element={<HousePage />} />

<Route path="/us/houseofreps" element={<HouseOfRepsMembersPage />} />
<Route path="/us/representative/:bioGuideId" element={<MemberDetailPage />} />
<Route path="/us/senators/" element={<SenateMembersPage />} />
<Route path="/us/senator/:bioGuideId" element={<MemberDetailPage />} />

{/* Presidents */}
<Route path="/us/presidents" element={<PresidentsPage />} />
<Route path="/us/president/:id" element={<PresidentDetailPage />} />


<Route path="/us/vicepresidents" element={<VicePresidentsPage />} />
<Route path="/us/vicepresident/:id" element={<VicePresidentDetailPage />} />
{/* Supreme Court Justices */}
<Route path="/us/justices" element={<JusticesPage />} />
<Route path="/us/justice/:id" element={<JusticeDetailPage />} />

<Route path="/us/whitehouse" element={<MainDepartmentPage country="us" department="whitehouse" />} />

<Route
  path="/us/candidate/local/:stateAbbr/:level/:ref"
  element={<LocalCandidateDetailPage />}
/>

          {/* US Department Routes */}
          <Route path="/us/departmentofcommerce" element={<MainDepartmentPage country="us" department="departmentofcommerce" />} />
          <Route path="/us/departmentofcommerce/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofhealth" element={<MainDepartmentPage country="us" department="departmentofhealth" />} />
          <Route path="/us/departmentofhealth/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofeducation" element={<MainDepartmentPage country="us" department="departmentofeducation" />} />
          <Route path="/us/departmentofeducation/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofhousing" element={<MainDepartmentPage country="us" department="departmentofhousing" />} />
          <Route path="/us/departmentofhousing/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofjustice" element={<MainDepartmentPage country="us" department="departmentofjustice" />} />
          <Route path="/us/departmentofjustice/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofenergy" element={<MainDepartmentPage country="us" department="departmentofenergy" />} />
          <Route path="/us/departmentofenergy/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentoftransportation" element={<MainDepartmentPage country="us" department="departmentoftransportation" />} />
          <Route path="/us/departmentoftransportation/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentoftreasury" element={<MainDepartmentPage country="us" department="departmentoftreasury" />} />
          <Route path="/us/departmentoftreasury/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentoflabor" element={<MainDepartmentPage country="us" department="departmentoflabor" />} />
          <Route path="/us/departmentoflabor/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentoftheinterior" element={<MainDepartmentPage country="us" department="departmentoftheinterior" />} />
          <Route path="/us/departmentoftheinterior/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofhomeland" element={<MainDepartmentPage country="us" department="departmentofhomeland" />} />
          <Route path="/us/departmentofhomeland/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofstate" element={<MainDepartmentPage country="us" department="departmentofstate" />} />
          <Route path="/us/departmentofstate/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofveteransaffairs" element={<MainDepartmentPage country="us" department="departmentofveteransaffairs" />} />
          <Route path="/us/departmentofveteransaffairs/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofdefense" element={<MainDepartmentPage country="us" department="departmentofdefense" />} />
          <Route path="/us/departmentofdefense/:agencyId" element={<AgencyPage country="us" />} />
          
          <Route path="/us/departmentofagriculture" element={<MainDepartmentPage country="us" department="departmentofagriculture" />} />
          <Route path="/us/departmentofagriculture/:agencyId" element={<AgencyPage country="us" />} />
  

          <Route path="/us/representatives" element={<RepresentativesPage />} />

          {/* ==== CANADA ROUTES ==== */}
          <Route path="/canada" element={<CanadaHomePage />} />

          {/* Canada Topic Routes — home page view with the topic preselected */}
          {CA_SECTION_PATHS.map((path) => (
            <Route key={path} path={path} element={<CanadaHomePage />} />
          ))}

          {/* Canada sections without a home-page view yet — still SectionPage */}
          <Route path="/canada/defense" element={
            <SectionPage
              country="ca"
              sectionKey="defence"
              sectionInfo={getSectionsInfo('ca').defence}
              feedsConfig={getFeedsConfig('ca')}
              maxItemsPerFeed={1}
            />
          } />

          <Route path="/canada/health" element={
            <SectionPage
              country="ca"
              sectionKey="health"
              sectionInfo={getSectionsInfo('ca').health}
              feedsConfig={getFeedsConfig('ca')}
              maxItemsPerFeed={1}
            />
          } />

          <Route path="/canada/immigration" element={
            <SectionPage
              country="ca"
              sectionKey="immigration"
              sectionInfo={getSectionsInfo('ca').immigration}
              feedsConfig={getFeedsConfig('ca')}
              maxItemsPerFeed={1}
            />
          } />

          <Route path="/canada/infrastructure" element={
            <SectionPage
              country="ca"
              sectionKey="infrastructure"
              sectionInfo={getSectionsInfo('ca').infrastructure}
              feedsConfig={getFeedsConfig('ca')}
              maxItemsPerFeed={1}
            />
          } />

          <Route path="/canada/justice" element={
            <SectionPage
              country="ca"
              sectionKey="justice"
              sectionInfo={getSectionsInfo('ca').justice}
              feedsConfig={getFeedsConfig('ca')}
              maxItemsPerFeed={1}
            />
          } />

         
          <Route path="/canada/prime-minister" element={<MainDepartmentPage country="ca" department="prime-minister" />} />
          <Route path="/canada/canada-revenue-agency" element={<MainDepartmentPage country="ca" department="canada-revenue-agency" />} />
          <Route path="/canada/impact-assessment-agency" element={<MainDepartmentPage country="ca" department="impact-assessment-agency" />} />
          <Route path="/canada/canadian-food-inspection-agency" element={<MainDepartmentPage country="ca" department="canadian-food-inspection-agency" />} />
          <Route path="/canada/atlantic-canada-opportunities-agency" element={<MainDepartmentPage country="ca" department="atlantic-canada-opportunities-agency" />} />
          <Route path="/canada/federal-economic-development-southern-ontario" element={<MainDepartmentPage country="ca" department="federal-economic-development-southern-ontario" />} />
          <Route path="/canada/canada-border-services-agency" element={<MainDepartmentPage country="ca" department="canada-border-services-agency" />} />
          <Route path="/canada/canadian-transportation-agency" element={<MainDepartmentPage country="ca" department="canadian-transportation-agency" />} />
          <Route path="/canada/canadian-northern-economic-development-agency" element={<MainDepartmentPage country="ca" department="canadian-northern-economic-development-agency" />} />
          <Route path="/canada/financial-consumer-agency" element={<MainDepartmentPage country="ca" department="financial-consumer-agency" />} />
          <Route path="/canada/public-health-agency" element={<MainDepartmentPage country="ca" department="public-health-agency" />} />
          <Route path="/canada/canadian-space-agency" element={<MainDepartmentPage country="ca" department="canadian-space-agency" />} />

          {/* ==== MEXICO ROUTES ==== */}
          <Route path="/mexico" element={<MexicoHomePage />} />

          {/* Mexico Topic Routes — home page view with the topic preselected */}
          {MX_SECTION_PATHS.map((path) => (
            <Route key={path} path={path} element={<MexicoHomePage />} />
          ))}

          {/* Mexico sections without a home-page view yet — still SectionPage */}
          <Route path="/mexico/immigration" element={
            <SectionPage
              country="mx"
              sectionKey="immigration"
              sectionInfo={getSectionsInfo('mx').immigration}
              feedsConfig={getFeedsConfig('mx')}
              maxItemsPerFeed={1}
            />
          } />

          <Route path="/mexico/justice" element={
            <SectionPage
              country="mx"
              sectionKey="justice"
              sectionInfo={getSectionsInfo('mx').justice}
              feedsConfig={getFeedsConfig('mx')}
              maxItemsPerFeed={1}
            />
          } />

          

          <Route path="/mexico/:stateName" element={<MexicoStatePage />} />

          <Route path="/mexico/presidencia" element={<MainDepartmentPage country="mx" department="presidencia" />} />
          <Route path="/mexico/secretaria-gobernacion" element={<MainDepartmentPage country="mx" department="secretaria-gobernacion" />} />
          <Route path="/mexico/secretaria-relaciones-exteriores" element={<MainDepartmentPage country="mx" department="secretaria-relaciones-exteriores" />} />
          <Route path="/mexico/secretaria-hacienda" element={<MainDepartmentPage country="mx" department="secretaria-hacienda" />} />
          <Route path="/mexico/secretaria-defensa" element={<MainDepartmentPage country="mx" department="secretaria-defensa" />} />
          <Route path="/mexico/secretaria-marina" element={<MainDepartmentPage country="mx" department="secretaria-marina" />} />
          <Route path="/mexico/secretaria-seguridad" element={<MainDepartmentPage country="mx" department="secretaria-seguridad" />} />
          <Route path="/mexico/secretaria-economia" element={<MainDepartmentPage country="mx" department="secretaria-economia" />} />
          <Route path="/mexico/secretaria-agricultura" element={<MainDepartmentPage country="mx" department="secretaria-agricultura" />} />
          <Route path="/mexico/secretaria-comunicaciones" element={<MainDepartmentPage country="mx" department="secretaria-comunicaciones" />} />
          <Route path="/mexico/secretaria-bienestar" element={<MainDepartmentPage country="mx" department="secretaria-bienestar" />} />
          <Route path="/mexico/secretaria-salud" element={<MainDepartmentPage country="mx" department="secretaria-salud" />} />
          <Route path="/mexico/secretaria-trabajo" element={<MainDepartmentPage country="mx" department="secretaria-trabajo" />} />
          <Route path="/mexico/secretaria-medio-ambiente" element={<MainDepartmentPage country="mx" department="secretaria-medio-ambiente" />} />
          <Route path="/mexico/secretaria-funcion-publica" element={<MainDepartmentPage country="mx" department="secretaria-funcion-publica" />} />
          <Route path="/mexico/secretaria-turismo" element={<MainDepartmentPage country="mx" department="secretaria-turismo" />} />

          {/* ==== UK ROUTES ==== */}
          <Route path="/uk" element={<UKHomePage/>} />

          {/* UK Topic Routes — home page view with the topic preselected */}
          {UK_SECTION_PATHS.map((path) => (
            <Route key={path} path={path} element={<UKHomePage />} />
          ))}

          <Route path="/uk/prime-ministers-office" element={<MainDepartmentPage country="uk" department="prime-ministers-office" />} />
          <Route path="/uk/hm-treasury" element={<MainDepartmentPage country="uk" department="hm-treasury" />} />
          <Route path="/uk/cabinet-office" element={<MainDepartmentPage country="uk" department="cabinet-office" />} />
          <Route path="/uk/foreign-commonwealth-development-office" element={<MainDepartmentPage country="uk" department="foreign-commonwealth-development-office" />} />
          <Route path="/uk/home-office" element={<MainDepartmentPage country="uk" department="home-office" />} />
          <Route path="/uk/ministry-of-defence" element={<MainDepartmentPage country="uk" department="ministry-of-defence" />} />
          <Route path="/uk/ministry-of-justice" element={<MainDepartmentPage country="uk" department="ministry-of-justice" />} />
          <Route path="/uk/department-of-health-and-social-care" element={<MainDepartmentPage country="uk" department="department-of-health-and-social-care" />} />
          <Route path="/uk/department-for-education" element={<MainDepartmentPage country="uk" department="department-for-education" />} />
          <Route path="/uk/department-for-transport" element={<MainDepartmentPage country="uk" department="department-for-transport" />} />
          <Route path="/uk/department-for-business-and-trade" element={<MainDepartmentPage country="uk" department="department-for-business-and-trade" />} />
          <Route path="/uk/department-for-work-and-pensions" element={<MainDepartmentPage country="uk" department="department-for-work-and-pensions" />} />
          <Route path="/uk/department-for-environment-food-rural-affairs" element={<MainDepartmentPage country="uk" department="department-for-environment-food-rural-affairs" />} />
          <Route path="/uk/department-for-energy-security-and-net-zero" element={<MainDepartmentPage country="uk" department="department-for-energy-security-and-net-zero" />} />
          <Route path="/uk/department-for-science-innovation-and-technology" element={<MainDepartmentPage country="uk" department="department-for-science-innovation-and-technology" />} />
          <Route path="/uk/attorney-generals-office" element={<MainDepartmentPage country="uk" department="attorney-generals-office" />} />
          <Route path="/uk/department-for-levelling-up-housing-and-communities" element={<MainDepartmentPage country="uk" department="department-for-levelling-up-housing-and-communities" />} />
          <Route path="/uk/:regionName" element={<UKKingdomPage />} />

          {/* ==== NEW COUNTRY PLACEHOLDERS ==== */}
          <Route path="/australia" element={<NewCountryHomePage />} />
          <Route path="/germany" element={<NewCountryHomePage />} />
          <Route path="/france" element={<NewCountryHomePage />} />
          <Route path="/italy" element={<NewCountryHomePage />} />
          <Route path="/israel" element={<NewCountryHomePage />} />
          <Route path="/ukraine" element={<NewCountryHomePage />} />
          <Route path="/china" element={<NewCountryHomePage />} />
          <Route path="/japan" element={<NewCountryHomePage />} />
          <Route path="/nigeria" element={<NewCountryHomePage />} />
          <Route path="/south-africa" element={<NewCountryHomePage />} />

          {/* Generic routes that work for all countries */}
          <Route path="/feed/:schema/:table" element={<FullFeedPage />} />
          <Route path="/feed/:schema/:table/sort/:sortBy" element={<FullFeedPage />} />
          <Route path="/us/recents" element={<RecentsPage />} />
<Route path="/canada/recents" element={<RecentsPage />} />
<Route path="/mexico/recents" element={<RecentsPage />} />
<Route path="/uk/recents" element={<RecentsPage />} />
<Route path="/:schema/recents" element={<RecentsPage />} />
          <Route path="/feed/:schema/:table/:id" element={<FeedItemDetail />} />
          <Route path="/search/:country" element={<SearchPage />} />
          <Route path="/search" element={<SearchPage />} />
                   {/* US State Routes */}
                   <Route path="/us/:stateName" element={<StatePage />} />
          <Route path="/us/:stateName/legislative" element={<StatePage section="legislative" />} />
          <Route path="/us/:stateName/executive" element={<StatePage section="executive" />} />
          <Route path="/us/:stateName/judicial" element={<StatePage section="judicial" />} />
          {/* Fallback route for other department routes */}
          <Route path="/canada/:provinceName" element={<ProvincePage/>} />

          <Route path="/*" element={<DepartmentRoutes />} />
          </Route>
        </Routes>
        
        {/* Conditionally render NavbarWrapper on all pages except onboarding */}
        <ConditionalNavbarWrapper />
   
   
     
    </AnimatePresence>
  );
};

function App() {

  

  useEffect(() => {
    // Run migration once on app startup
    const migrationResult = migrateOldCollection();
    if (migrationResult.migrated) {
      console.log(`Migrated ${migrationResult.count} items from old format`);
    }
  }, []);

  useEffect(() => {
    document.body.classList.add('theme-loaded');
  }, []);

  useEffect(() => {
    const savedFont = localStorage.getItem('font');
    if (savedFont) {
      document.documentElement.setAttribute('data-font', savedFont);
    }
  }, []);
  
  
  return (
    <ThemeProvider>
    <Router>
      <SearchProvider>
        <ScrollToTop />
        <div>
          <AppLayout>
            <AnimatedRoutes />
          </AppLayout>
        </div>
      </SearchProvider>
    </Router>
  </ThemeProvider>
  );
}


export default App;